import { io, Socket } from 'socket.io-client';
import { REACT_APP_API_URL } from '../app/app.config';

type RoomKey = 'identity' | 'commercialTransactionId';

let socket: Socket | null = null;
let connectionRefCount = 0;

const joinedRooms: Record<RoomKey, Map<string, number>> = {
    identity: new Map(),
    commercialTransactionId: new Map(),
};

const stationUpdatedListeners = new Set<(identity: string) => void>();
const commercialTransactionUpdatedListeners = new Set<(id: string) => void>();

function getSocketUrl(): string {
    return `${REACT_APP_API_URL?.replace('http', 'ws').replace('https', 'wss') || ''}`;
}

function ensureSocket(): Socket {
    if (!socket) {
        socket = io(getSocketUrl(), {
            transports: ['websocket', 'polling'],
            path: '/commercial-io',
        });

        socket.on('station_updated', (data: { identity?: string }) => {
            const identity = data?.identity;
            if (identity) {
                stationUpdatedListeners.forEach(cb => cb(identity));
            }
        });

        socket.on('commercial_transaction_updated', (data: { id?: string }) => {
            const id = data?.id;
            if (id) {
                commercialTransactionUpdatedListeners.forEach(cb => cb(id));
            }
        });

        socket.on('connect', () => {
            (Object.keys(joinedRooms) as RoomKey[]).forEach(key => {
                joinedRooms[key].forEach((_refCount, value) => {
                    socket?.emit('join', { [key]: value });
                });
            });
        });
    }

    return socket;
}

const joinRoom = (key: RoomKey, value: string): void => {
    const sock = ensureSocket();
    const rooms = joinedRooms[key];
    const refCount = rooms.get(value) ?? 0;
    rooms.set(value, refCount + 1);

    if (refCount === 0 && sock.connected) {
        sock.emit('join', { [key]: value });
    }
};

const leaveRoom = (key: RoomKey, value: string): void => {
    const rooms = joinedRooms[key];
    const refCount = rooms.get(value) ?? 0;
    if (!refCount) return;

    if (refCount > 1) {
        rooms.set(value, refCount - 1);
        return;
    }

    rooms.delete(value);
    if (socket?.connected) {
        socket.emit('leave', { [key]: value });
    }
};

const addListener = <T>(listeners: Set<T>, cb: T): (() => void) => {
    listeners.add(cb);
    return () => {
        listeners.delete(cb);
    };
};

export const acquireSocketConnection = (): void => {
    ensureSocket();
    connectionRefCount++;
};

export const releaseSocketConnection = (): void => {
    connectionRefCount = Math.max(0, connectionRefCount - 1);
    if (connectionRefCount === 0 && socket) {
        socket.disconnect();
        socket = null;
        (Object.keys(joinedRooms) as RoomKey[]).forEach(key => joinedRooms[key].clear());
    }
};

// stations
export const joinStations = (identities: string[]): void =>
    identities.forEach(identity => joinRoom('identity', identity));

export const leaveStations = (identities: string[]): void =>
    identities.forEach(identity => leaveRoom('identity', identity));

export const onStationUpdated = (cb: (identity: string) => void): (() => void) =>
    addListener(stationUpdatedListeners, cb);

// commercial transactions
export const joinCommercialTransaction = (id: string): void => joinRoom('commercialTransactionId', id);

export const leaveCommercialTransaction = (id: string): void => leaveRoom('commercialTransactionId', id);

export const onCommercialTransactionUpdated = (cb: (id: string) => void): (() => void) =>
    addListener(commercialTransactionUpdatedListeners, cb);
